import type { NoteWorkingDraft, NoteWorkingDraftIntent } from "@emr/contracts";
import { createContext, useContext } from "react";
import type { NoteSectionsWrite } from "../api/clinicalWorkspace";

/**
 * The persisted visit's note between commits: the clinician's working draft on the server (one
 * overwritten row per clinician per visit, see `note-working-draft.ts` in contracts). Saving it
 * writes no note version and leaves the encounter version alone. Null without a working-draft provider.
 */

/** What the record of working drafts holds for the note on screen. */
export type NoteDraftStatus =
  | { readonly state: "saved" }
  /** Changed on screen; saved after a pause in typing. */
  | { readonly state: "editing" }
  | { readonly state: "saving" }
  | {
      readonly state: "failed";
      readonly message: string;
      readonly offline: boolean;
      /** The visit was signed or closed elsewhere: trying again cannot help. */
      readonly final?: boolean;
    }
  | { readonly state: "review"; readonly reason: NoteDraftReview["reason"] };

/**
 * Two versions of the note that cannot both be right. `found`: a draft on the server differs from
 * the note on screen when the visit opens. `changed`: another tab or device saved the draft since
 * this one did. `record`: the saved note changed after the draft on screen was written. Autosave
 * waits while it is open, so neither version is overwritten without the clinician choosing.
 */
export interface NoteDraftReview {
  readonly reason: "found" | "changed" | "record";
  /** The note on screen. */
  readonly mine: NoteSectionsWrite;
  /** The other version: the server draft (found, changed) or the saved note (record). */
  readonly other: NoteSectionsWrite;
  /** When the other version was saved, when known. */
  readonly otherSavedAt: string | null;
}

export interface NoteWorkingDraftSurface {
  readonly status: NoteDraftStatus;
  readonly review: NoteDraftReview | null;
  /** Saves the note on screen now (after a failure). */
  retry(): void;
  /** Settles a review: keep the note on screen, or take the other version. */
  resolveReview(choice: "mine" | "other"): Promise<void>;
  /** Sends a waiting change now; true once the server holds the note on screen. */
  confirmSaved(): Promise<boolean>;
}

export const PersistedNoteWorkingDraftContext = createContext<NoteWorkingDraftSurface | null>(null);

export function usePersistedNoteWorkingDraft(): NoteWorkingDraftSurface | null {
  return useContext(PersistedNoteWorkingDraftContext);
}

export type { NoteWorkingDraft, NoteWorkingDraftIntent };

// ---------------------------------------------------------------------------
// What this browser last saved, per visit
// ---------------------------------------------------------------------------

const SYNC_PREFIX = "bobdoc.note-draft-sync.v1:";
const UNSAVED_PREFIX = "bobdoc.note-draft-unsaved.v1:";

/**
 * The draft revision this browser last saved or adopted for a visit, and a fingerprint of that
 * note's text (never the text itself). Tells a reopened visit whether the note this browser kept is
 * already on the server, and whether the server's draft moved on since.
 */
export interface NoteDraftSyncMarker {
  readonly revision: number;
  readonly fingerprint: string;
}

export function readNoteDraftSync(encounterId: string): NoteDraftSyncMarker | null {
  try {
    const raw = window.localStorage.getItem(`${SYNC_PREFIX}${encounterId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NoteDraftSyncMarker>;
    return typeof parsed.revision === "number" && typeof parsed.fingerprint === "string"
      ? { revision: parsed.revision, fingerprint: parsed.fingerprint }
      : null;
  } catch {
    return null;
  }
}

export function writeNoteDraftSync(encounterId: string, marker: NoteDraftSyncMarker): void {
  try {
    window.localStorage.setItem(`${SYNC_PREFIX}${encounterId}`, JSON.stringify(marker));
  } catch {
    /* Keep working without browser storage; the server copy is the safeguard. */
  }
}

/** Clears one visit's marker, or (at sign-out) every visit's markers and unsaved marks. */
export function clearNoteDraftSync(encounterId?: string): void {
  try {
    if (encounterId !== undefined) {
      window.localStorage.removeItem(`${SYNC_PREFIX}${encounterId}`);
      return;
    }
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(SYNC_PREFIX) || key?.startsWith(UNSAVED_PREFIX))
        window.localStorage.removeItem(key);
    }
  } catch {
    /* Nothing to clear without browser storage. */
  }
}

/**
 * Marks a visit whose note holds changes the server does not have yet (the browser copy is then the
 * only one), and unmarks it once the server has them. The mark outlives the visit's page, so
 * signing out still warns after the visit was closed or the browser reloaded.
 */
export function markNoteDraftUnsaved(encounterId: string, unsaved: boolean): void {
  try {
    if (unsaved) window.localStorage.setItem(`${UNSAVED_PREFIX}${encounterId}`, "1");
    else window.localStorage.removeItem(`${UNSAVED_PREFIX}${encounterId}`);
  } catch {
    /* Without browser storage there is no browser copy to lose either. */
  }
}

/** True while a visit in this browser holds note changes the server does not have. */
export function hasUnsavedNoteDrafts(): boolean {
  try {
    for (let index = 0; index < window.localStorage.length; index += 1)
      if (window.localStorage.key(index)?.startsWith(UNSAVED_PREFIX)) return true;
  } catch {
    /* Without browser storage there is no browser copy to lose. */
  }
  return false;
}

/** A short, stable fingerprint of the note's four sections (cyrb53). */
export function noteFingerprint(note: NoteSectionsWrite): string {
  const text = JSON.stringify([note.subjective, note.objective, note.assessment, note.plan]);
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return `${(h2 >>> 0).toString(16).padStart(8, "0")}${(h1 >>> 0).toString(16).padStart(8, "0")}-${text.length}`;
}

// ---------------------------------------------------------------------------
// Sending waiting saves before the session ends
// ---------------------------------------------------------------------------

const flushers = new Set<() => Promise<boolean>>();

/** Each open persisted visit registers how to send its waiting save; returns the unregister. */
export function registerNoteDraftFlusher(flush: () => Promise<boolean>): () => void {
  flushers.add(flush);
  return () => {
    flushers.delete(flush);
  };
}

/**
 * Sends every open visit's waiting save. True when every note on screen reached the server within
 * `timeoutMs`; false when one could not (offline, refused, or slow).
 */
export async function flushNoteDrafts(timeoutMs: number): Promise<boolean> {
  if (flushers.size === 0) return true;
  const all = Promise.all([...flushers].map((flush) => flush().catch(() => false))).then(
    (results) => results.every(Boolean),
  );
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), timeoutMs);
  });
  try {
    return await Promise.race([all, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Which version a reopened visit shows
// ---------------------------------------------------------------------------

export type NoteDraftRestore =
  /** No server draft: keep what hydration showed (unsynced browser edits are saved next). */
  | { readonly kind: "none" }
  /** No server draft, and the note this browser kept was already saved: someone settled it. */
  | { readonly kind: "use-record" }
  /** The saved note already holds the draft: delete the draft. */
  | { readonly kind: "discard" }
  /** The draft continues the saved note: show it. */
  | { readonly kind: "restore" }
  /** The note on screen is the draft already. */
  | { readonly kind: "current" }
  /** This browser's unsaved edits continue the draft it last saved: keep them, save next. */
  | { readonly kind: "keep-local" }
  | { readonly kind: "review"; readonly reason: "found" | "record" };

/**
 * Decides, once per opening, which version of the note a persisted visit shows. Every input is a
 * fingerprint or version number, so the rule can be checked without a browser.
 */
export function decideNoteDraftRestore(input: {
  readonly record: { readonly fingerprint: string; readonly noteVersion: number };
  readonly local: string;
  readonly marker: NoteDraftSyncMarker | null;
  readonly draft: {
    readonly fingerprint: string;
    readonly revision: number;
    readonly baseNoteVersion: number;
  } | null;
}): NoteDraftRestore {
  const { record, local, marker, draft } = input;
  const localSynced = marker !== null && marker.fingerprint === local;
  if (draft === null) {
    return localSynced && local !== record.fingerprint ? { kind: "use-record" } : { kind: "none" };
  }
  if (draft.fingerprint === record.fingerprint) return { kind: "discard" };
  const recordMoved = draft.baseNoteVersion !== record.noteVersion;
  if (local === draft.fingerprint)
    return recordMoved ? { kind: "review", reason: "record" } : { kind: "current" };
  if (!recordMoved) {
    const localPlain = local === record.fingerprint;
    if (localPlain || (localSynced && draft.revision > (marker?.revision ?? 0)))
      return { kind: "restore" };
    if (!localSynced && marker !== null && draft.revision === marker.revision)
      return { kind: "keep-local" };
  }
  return { kind: "review", reason: "found" };
}
